<template>
  <div class="template-setting-table-shell">
    <div class="table-responsive template-setting-table-wrap">
      <table class="table table-hover align-middle mb-0 template-setting-table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="`header-${column.field}`"
              :class="['template-setting-table-head', column.headerClass]"
            >
              {{ column.label }}
            </th>
          </tr>
          <tr class="template-setting-filter-row">
            <th
              v-for="column in columns"
              :key="`filter-${column.field}`"
              :class="['template-setting-table-filter-cell', column.headerClass]"
            >
              <input
                v-if="isFilterable(column)"
                v-model="filters[column.field]"
                type="text"
                class="form-control form-control-sm"
                :placeholder="column.filterOptions.placeholder || 'Filter'"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="displayRows.length === 0">
            <td :colspan="columns.length" class="text-center text-muted py-4">
              No data available
            </td>
          </tr>
          <tr
            v-for="(rowData, index) in displayRows"
            :key="rowData.key"
            class="template-setting-table-row"
          >
            <td
              v-for="column in columns"
              :key="`${rowData.key}-${column.field}`"
              :class="['template-setting-table-cell', column.cellClass]"
            >
              <slot
                name="cell"
                :column="column"
                :row="rowData.row"
                :index="index"
                :sourceIndex="rowData.sourceIndex"
                :value="getColumnValue(rowData.row, column.field)"
              >
                {{ getColumnValue(rowData.row, column.field) }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "TemplateSettingCustomTable",
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    sortField: {
      type: String,
      default: "",
    },
    sortType: {
      type: String,
      default: "asc",
    },
    rowKeyField: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      filters: {},
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    sortedRows() {
      if (!this.sortField) {
        return this.normalizedRows;
      }

      const direction = this.sortType === "desc" ? -1 : 1;
      return [...this.normalizedRows].sort((left, right) => {
        const leftValue = this.getComparableValue(
          this.getColumnValue(left, this.sortField),
        );
        const rightValue = this.getComparableValue(
          this.getColumnValue(right, this.sortField),
        );

        if (leftValue < rightValue) {
          return -1 * direction;
        }

        if (leftValue > rightValue) {
          return 1 * direction;
        }

        return 0;
      });
    },
    filteredRows() {
      return this.sortedRows.filter((row) =>
        this.columns.every((column) => {
          if (!this.isFilterable(column)) {
            return true;
          }

          const keyword = (this.filters[column.field] || "").toString().trim().toLowerCase();
          if (!keyword) {
            return true;
          }

          const value = this.getColumnValue(row, column.field);
          return value.toString().toLowerCase().includes(keyword);
        }),
      );
    },
    displayRows() {
      return this.filteredRows.map((row, index) => ({
        row,
        sourceIndex: this.normalizedRows.indexOf(row),
        key: this.buildRowKey(row, index),
      }));
    },
  },
  watch: {
    columns: {
      deep: true,
      handler() {
        this.initializeFilters();
      },
    },
  },
  created() {
    this.initializeFilters();
  },
  methods: {
    initializeFilters() {
      const nextFilters = {};
      this.columns.forEach((column) => {
        if (this.isFilterable(column)) {
          nextFilters[column.field] =
            this.filters[column.field] || column.filterOptions.filterValue || "";
        }
      });
      this.filters = nextFilters;
    },
    isFilterable(column) {
      return Boolean(
        column &&
          column.filterOptions &&
          column.filterOptions.enabled,
      );
    },
    getColumnValue(row, field) {
      if (
        field === "field_lable" &&
        row &&
        row.field &&
        row.field.field_lable
      ) {
        return row.field.field_lable;
      }

      const value = row ? row[field] : "";
      return value == null ? "" : value;
    },
    getComparableValue(value) {
      if (typeof value === "number") {
        return value;
      }

      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && value !== "") {
        return numericValue;
      }

      return value.toString().toLowerCase();
    },
    buildRowKey(row, fallbackIndex) {
      if (this.rowKeyField && row && row[this.rowKeyField] != null) {
        return `${this.rowKeyField}-${row[this.rowKeyField]}`;
      }

      if (row && row.proforma_invoice_template_field_list_id != null) {
        return `field-${row.proforma_invoice_template_field_list_id}`;
      }

      if (
        row &&
        row.field &&
        row.field.proforma_invoice_template_field_list_id != null
      ) {
        return `selected-${row.field.proforma_invoice_template_field_list_id}`;
      }

      return `row-${fallbackIndex}`;
    },
  },
};
</script>

<style scoped>
.template-setting-table-shell {
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.template-setting-table-wrap {
  max-height: 600px;
  overflow: auto;
}

.template-setting-table {
  min-width: 100%;
}

.template-setting-table thead th {
  position: sticky;
  z-index: 3;
  background: #f8fafc;
}

.template-setting-table thead tr:first-child th {
  top: 0;
  box-shadow: inset 0 -1px 0 #d9e2ec;
}

.template-setting-table thead tr:nth-child(2) th {
  top: 48px;
  z-index: 2;
  box-shadow: inset 0 -1px 0 #e5edf5;
}

.template-setting-table-head {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #3f4d67;
  padding: 0.9rem 0.85rem;
  white-space: nowrap;
}

.template-setting-table-filter-cell {
  padding: 0.6rem 0.85rem;
  min-width: 150px;
}

.template-setting-table-cell {
  padding: 0.85rem;
  vertical-align: middle;
}

.template-setting-col-description {
  width: 280px;
  min-width: 280px;
  max-width: 280px;
}

.template-setting-col-description.template-setting-table-cell {
  white-space: normal;
  word-break: break-word;
}

.template-setting-table-row:hover {
  background: #f8fbff;
}

@media (max-width: 767.98px) {
  .template-setting-table-wrap {
    max-height: 360px;
  }

  .template-setting-table thead tr:nth-child(2) th {
    top: 44px;
  }

  .template-setting-col-description {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
  }
}
</style>
